import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import { UserDataFirebasePatchRequest, UserDataFirebaseRequest } from '../../types';
import { DeleteUsersApiResponse, DeleteUsersResult, USERS_QUERY_KEY } from './types';

type CreateParams = { data: UserDataFirebaseRequest };
type UpdateParams = { data: UserDataFirebasePatchRequest };
/** 1..N uid — usunięcie pojedyncze to tablica jednoelementowa. */
type DeleteParams = { uids: string[] };
type ResendParams = { email: string };

// Tworzy konto w Firebase Auth + dokument `users/{uid}` i wysyła mail powitalny
// (info o koncie + link do „Nie pamiętam hasła"). Bez tokenów zaproszeń.
export const useCreateUser = (
  options?: UseMutationOptions<void, AxiosError, CreateParams>,
): UseMutationResult<void, AxiosError, CreateParams> => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, CreateParams>({
    mutationFn: async ({ data }) => {
      await axiosInstance.post('/v3-createUser', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
    ...options,
  });
};

export const useUpdateUser = (
  options?: UseMutationOptions<void, AxiosError, UpdateParams>,
): UseMutationResult<void, AxiosError, UpdateParams> => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, UpdateParams>({
    mutationFn: async ({ data }) => {
      await axiosInstance.put('/v3-updateUser', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
    ...options,
  });
};

// Usuwa użytkownika z `users` i z Firebase Auth. Single + bulk tym samym endpointem.
export const useDeleteUsers = (
  options?: UseMutationOptions<DeleteUsersResult, AxiosError, DeleteParams>,
): UseMutationResult<DeleteUsersResult, AxiosError, DeleteParams> => {
  const queryClient = useQueryClient();

  return useMutation<DeleteUsersResult, AxiosError, DeleteParams>({
    mutationFn: async ({ uids }) => {
      const res = await axiosInstance.delete<DeleteUsersApiResponse>('/v3-deleteUser', {
        data: { uids },
      });
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
    ...options,
  });
};

// Wysyła użytkownikowi mail z jednorazowym linkiem do ustawienia nowego hasła
// (koperta w tabeli /users). Link generuje backend przez Firebase Auth.
export const useSendPasswordResetLink = (
  options?: UseMutationOptions<void, AxiosError, ResendParams>,
): UseMutationResult<void, AxiosError, ResendParams> => useMutation<
  void,
  AxiosError,
  ResendParams
>({
  mutationFn: async ({ email }) => {
    await axiosInstance.post('/v3-sendPasswordResetLink', { email });
  },
  ...options,
});

// Ponownie wysyła mail powitalny (dawny „resend invite" — już bez tokenu).
export const useResendWelcomeEmail = (
  options?: UseMutationOptions<void, AxiosError, ResendParams>,
): UseMutationResult<void, AxiosError, ResendParams> => useMutation<
  void,
  AxiosError,
  ResendParams
>({
  mutationFn: async ({ email }) => {
    await axiosInstance.post('/v3-resendWelcomeEmail', { email });
  },
  ...options,
});
